import { FC } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  SimpleGrid,
  NativeSelectRoot,
  NativeSelectField,
  Fieldset,
} from '@chakra-ui/react';
import { DogSearchFilter, Gender, DogStatus } from '@/types/dog';
import {
  VaccinationType,
  VACCINATION_TYPES,
  VACCINATION_TYPE_LABELS,
} from '@/types/vaccination';

interface DogSearchFormProps {
  onSearch: (filter: DogSearchFilter) => void;
  onClear: () => void;
  loading?: boolean;
}

interface SearchFormData {
  name: string;
  bioNumber: string;
  registrationNumber: string;
  gender: Gender | '';
  status: DogStatus | '';
  birthDateFrom: string;
  birthDateTo: string;
  matingPeriodFrom: string;
  matingPeriodTo: string;
  hasMatingRecord: boolean;
  hasBirthRecord: boolean;
  isBreedingAvailable: boolean;
  unvaccinatedTypes: VaccinationType[];
  unvaccinatedPeriodFrom: string;
  unvaccinatedPeriodTo: string;
}

export const DogSearchForm: FC<DogSearchFormProps> = ({ onSearch, onClear, loading }) => {
  const { register, handleSubmit, reset } = useForm<SearchFormData>({
    defaultValues: {
      name: '',
      bioNumber: '',
      registrationNumber: '',
      gender: '',
      status: '',
      birthDateFrom: '',
      birthDateTo: '',
      matingPeriodFrom: '',
      matingPeriodTo: '',
      hasMatingRecord: false,
      hasBirthRecord: false,
      isBreedingAvailable: false,
      unvaccinatedTypes: [],
      unvaccinatedPeriodFrom: '',
      unvaccinatedPeriodTo: '',
    },
  });

  const onSubmit = (data: SearchFormData) => {
    const filter: DogSearchFilter = {};

    // 文字列フィールド（空文字以外）
    if (data.name.trim()) filter.name = data.name.trim();
    if (data.bioNumber.trim()) filter.bioNumber = data.bioNumber.trim();
    if (data.registrationNumber.trim())
      filter.registrationNumber = data.registrationNumber.trim();

    // 選択フィールド
    if (data.gender) filter.gender = data.gender as Gender;
    if (data.status) filter.status = data.status as DogStatus;

    // 日付フィールド
    if (data.birthDateFrom) filter.birthDateFrom = new Date(data.birthDateFrom);
    if (data.birthDateTo) filter.birthDateTo = new Date(data.birthDateTo);

    // 交配時期フィールド
    if (data.matingPeriodFrom) filter.matingPeriodFrom = new Date(data.matingPeriodFrom);
    if (data.matingPeriodTo) filter.matingPeriodTo = new Date(data.matingPeriodTo);

    // チェックボックス（trueのみ）
    if (data.hasMatingRecord) filter.hasMatingRecord = true;
    if (data.hasBirthRecord) filter.hasBirthRecord = true;
    if (data.isBreedingAvailable) filter.isBreedingAvailable = true;

    // ワクチン未接種フィルタ
    if (data.unvaccinatedTypes && data.unvaccinatedTypes.length > 0) {
      filter.unvaccinatedTypes = data.unvaccinatedTypes;
      if (data.unvaccinatedPeriodFrom)
        filter.unvaccinatedPeriodFrom = new Date(data.unvaccinatedPeriodFrom);
      if (data.unvaccinatedPeriodTo)
        filter.unvaccinatedPeriodTo = new Date(data.unvaccinatedPeriodTo);
    }

    onSearch(filter);
  };

  const handleClear = () => {
    reset();
    onClear();
  };

  return (
    <Box bg="white" p={6} borderRadius="lg" shadow="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={6} align="stretch">
          <Text fontSize="lg" fontWeight="bold">
            検索条件
          </Text>

          {/* 基本情報検索 */}
          <Fieldset.Root>
            <Fieldset.Legend fontSize="md" fontWeight="semibold">
              基本情報
            </Fieldset.Legend>
            <Fieldset.Content>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {/* 名前 */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    名前
                  </Text>
                  <Input
                    {...register('name')}
                    placeholder="部分一致で検索"
                    size="sm"
                    disabled={loading}
                  />
                </Box>

                {/* 生体番号 */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    生体番号
                  </Text>
                  <Input
                    {...register('bioNumber')}
                    placeholder="例: DOG-2025-001"
                    size="sm"
                    disabled={loading}
                  />
                </Box>

                {/* 登録番号（JKC・DNA・マイクロチップ・暗証記号） */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    登録番号
                  </Text>
                  <Input
                    {...register('registrationNumber')}
                    placeholder="JKC / DNA / マイクロチップ / 暗証記号"
                    size="sm"
                    disabled={loading}
                  />
                </Box>

                {/* 性別 */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    性別
                  </Text>
                  <NativeSelectRoot size="sm" disabled={loading}>
                    <NativeSelectField {...register('gender')}>
                      <option value="">すべて</option>
                      <option value="male">オス</option>
                      <option value="female">メス</option>
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Box>

                {/* ステータス */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    ステータス
                  </Text>
                  <NativeSelectRoot size="sm" disabled={loading}>
                    <NativeSelectField {...register('status')}>
                      <option value="">すべて</option>
                      <option value="active">在籍中</option>
                      <option value="transferred">譲渡済み</option>
                      <option value="deceased">死亡</option>
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Box>
              </SimpleGrid>
            </Fieldset.Content>
          </Fieldset.Root>

          {/* 出生日検索 */}
          <Fieldset.Root>
            <Fieldset.Legend fontSize="md" fontWeight="semibold">
              出生日
            </Fieldset.Legend>
            <Fieldset.Content>
              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    開始日
                  </Text>
                  <Input {...register('birthDateFrom')} type="date" size="sm" disabled={loading} />
                </Box>
                <Box pt={6}>〜</Box>
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    終了日
                  </Text>
                  <Input {...register('birthDateTo')} type="date" size="sm" disabled={loading} />
                </Box>
              </HStack>
            </Fieldset.Content>
          </Fieldset.Root>

          {/* 交配時期検索 */}
          <Fieldset.Root>
            <Fieldset.Legend fontSize="md" fontWeight="semibold">
              交配時期
            </Fieldset.Legend>
            <Fieldset.Content>
              <VStack align="stretch" gap={4}>
                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      開始日
                    </Text>
                    <Input
                      {...register('matingPeriodFrom')}
                      type="date"
                      size="sm"
                      disabled={loading}
                    />
                  </Box>
                  <Box pt={6}>〜</Box>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      終了日
                    </Text>
                    <Input
                      {...register('matingPeriodTo')}
                      type="date"
                      size="sm"
                      disabled={loading}
                    />
                  </Box>
                </HStack>

                {/* 交配関連のチェックボックス */}
                <VStack align="stretch" gap={2}>
                  <HStack gap={2}>
                    <input type="checkbox" {...register('hasMatingRecord')} disabled={loading} />
                    <Text fontSize="sm">交配記録がある個体</Text>
                  </HStack>
                  <HStack gap={2}>
                    <input type="checkbox" {...register('hasBirthRecord')} disabled={loading} />
                    <Text fontSize="sm">出産記録がある個体</Text>
                  </HStack>
                  <HStack gap={2}>
                    <input type="checkbox" {...register('isBreedingAvailable')} disabled={loading} />
                    <Text fontSize="sm">交配可能な個体（メス犬のみ）</Text>
                  </HStack>
                </VStack>
              </VStack>
            </Fieldset.Content>
          </Fieldset.Root>

          {/* ワクチン未接種検索 */}
          <Fieldset.Root>
            <Fieldset.Legend fontSize="md" fontWeight="semibold">
              ワクチン未接種
            </Fieldset.Legend>
            <Fieldset.Content>
              <VStack align="stretch" gap={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    種類（指定期間内に接種記録がない個体を抽出）
                  </Text>
                  <VStack align="stretch" gap={2}>
                    {VACCINATION_TYPES.map((type) => (
                      <HStack key={type} gap={2}>
                        <input
                          type="checkbox"
                          value={type}
                          {...register('unvaccinatedTypes')}
                          disabled={loading}
                        />
                        <Text fontSize="sm">{VACCINATION_TYPE_LABELS[type]}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      期間（開始日）
                    </Text>
                    <Input
                      {...register('unvaccinatedPeriodFrom')}
                      type="date"
                      size="sm"
                      disabled={loading}
                    />
                  </Box>
                  <Box pt={6}>〜</Box>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      期間（終了日）
                    </Text>
                    <Input
                      {...register('unvaccinatedPeriodTo')}
                      type="date"
                      size="sm"
                      disabled={loading}
                    />
                  </Box>
                </HStack>
              </VStack>
            </Fieldset.Content>
          </Fieldset.Root>

          {/* ボタン */}
          <HStack justify="flex-end" gap={2}>
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              size="sm"
              disabled={loading}
            >
              クリア
            </Button>
            <Button type="submit" colorPalette="blue" size="sm" loading={loading}>
              検索
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};
